import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Download, Receipt, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { useFamilyAuth } from "../../context/FamilyAuthContext";

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function Card({ children, className = "" }) {
  return <section className={`family-card ${className}`}>{children}</section>;
}

function Loading() {
  return <div className="py-20 text-center text-sm text-[#72839a]">Caricamento delle informazioni…</div>;
}

function Message({ error }) {
  return error ? <p className="family-card border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null;
}

function EmptyState({ title, text }) {
  return (
    <Card className="p-7 text-center">
      <Receipt className="mx-auto h-7 w-7 text-[#8f7ac8]" />
      <p className="mt-3 font-bold text-[#304762]">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-[#72839a]">{text}</p>
    </Card>
  );
}

function PageHeader({ title, subtitle, backTo = "/family/altro" }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <Link to={backTo} className="mt-0.5 rounded-full p-1 text-[#314b6c] hover:bg-white">
        <ArrowLeft className="h-5 w-5" />
      </Link>
      <div>
        <h1 className="text-[22px] font-extrabold tracking-tight text-[#243952] sm:text-2xl">{title}</h1>
        {subtitle ? <p className="mt-0.5 text-xs text-[#687c94] sm:text-sm">{subtitle}</p> : null}
      </div>
    </div>
  );
}

function formatDate(value, options = { day: "2-digit", month: "short", year: "numeric" }) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("it-IT", options);
}

function formatMoney(value) {
  return `€ ${Number(value || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
}

function formatMonth(value) {
  if (!value) return "";
  const [year, month] = String(value).split("-");
  if (!year || !month) return value;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
}

function invoiceTitle(invoice) {
  const kind = invoice?.tipo_documento === "nota_credito" ? "Nota di credito" : "Fattura";
  return invoice?.numero ? `${kind} ${invoice.numero}` : kind;
}

const paymentTone = {
  pagata: "bg-emerald-50 text-emerald-700",
  parziale: "bg-amber-50 text-amber-700",
  scaduta: "bg-rose-50 text-rose-600",
  non_pagata: "bg-slate-50 text-[#536b86]",
};

function PaymentBadge({ invoice }) {
  const status = invoice?.stato_pagamento || "non_pagata";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${paymentTone[status] || paymentTone.non_pagata}`}>
      {invoice?.stato_pagamento_label || status.replaceAll("_", " ")}
    </span>
  );
}

async function fetchInvoicePdf(api, ospiteId, fatturaId) {
  const response = await api.get(
    `/ospiti/${ospiteId}/amministrazione/fatture/${fatturaId}/pdf`,
    { responseType: "blob" },
  );
  const blob = response.data;
  const type = String(blob?.type || "");
  if (type.includes("json")) {
    const text = await blob.text();
    let detail = "Fattura non disponibile";
    try {
      detail = JSON.parse(text).detail || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return type.includes("pdf") ? blob : new Blob([blob], { type: "application/pdf" });
}

function InvoicePdfViewer({ viewer, onClose, onSave }) {
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      URL.revokeObjectURL(viewer.url);
    };
  }, [viewer]);
  if (!viewer) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <button type="button" onClick={onClose} className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white" aria-label="Chiudi">
        <X className="h-5 w-5" />
      </button>
      {typeof window.FamilyApp?.saveFile === "function" ? (
        <button type="button" onClick={onSave} className="absolute right-16 top-4 z-10 rounded-full bg-white/15 px-3 py-2 text-xs font-bold text-white">
          Salva
        </button>
      ) : null}
      <p className="absolute bottom-5 left-1/2 z-10 max-w-[80%] -translate-x-1/2 truncate rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white">{viewer.name}</p>
      <iframe title={viewer.name} src={viewer.url} className="h-[88dvh] w-full max-w-3xl rounded-xl bg-white" onClick={(event) => event.stopPropagation()} />
    </div>
  );
}

function InvoiceDetail({ fatturaId }) {
  const navigate = useNavigate();
  const { api, activeOspiteId } = useFamilyAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    if (!activeOspiteId || !fatturaId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    api.get(`/ospiti/${activeOspiteId}/amministrazione/fatture/${fatturaId}`)
      .then((response) => {
        if (cancelled) return;
        setInvoice(response.data?.fattura || null);
        setError("");
      })
      .catch((err) => {
        if (cancelled) return;
        setInvoice(null);
        setError(err.response?.data?.detail || "Fattura non trovata");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, activeOspiteId, fatturaId]);

  const loadPdf = async () => {
    const blob = await fetchInvoicePdf(api, activeOspiteId, fatturaId);
    const name = `${invoiceTitle(invoice)}.pdf`;
    return { blob, name };
  };

  const openPdf = async () => {
    setBusy("open");
    try {
      const { blob, name } = await loadPdf();
      setViewer({ url: URL.createObjectURL(blob), name });
    } catch (err) {
      toast.error(err.response ? "Non è stato possibile aprire la fattura" : err.message || "Non è stato possibile aprire la fattura");
    } finally {
      setBusy("");
    }
  };

  const downloadPdf = async (fromViewer = false) => {
    if (fromViewer && viewer?.url && typeof window.FamilyApp?.saveFile === "function") {
      try {
        const blob = await fetch(viewer.url).then((res) => res.blob());
        window.FamilyApp.saveFile(viewer.name || "fattura.pdf", await blobToDataUrl(blob));
      } catch {
        toast.error("Non è stato possibile salvare la fattura");
      }
      return;
    }
    setBusy("download");
    try {
      const { blob, name } = await loadPdf();
      if (typeof window.FamilyApp?.saveFile === "function") {
        window.FamilyApp.saveFile(name, await blobToDataUrl(blob));
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error(err.response ? "Non è stato possibile scaricare la fattura" : err.message || "Non è stato possibile scaricare la fattura");
    } finally {
      setBusy("");
    }
  };

  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <PageHeader backTo="/family/amministrazione" title={invoice ? invoiceTitle(invoice) : "Fattura"} subtitle="Dettaglio e copia PDF." />
      <Message error={error} />
      {invoice ? (
        <>
          <Card className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-[#72839a]">{formatMonth(invoice.mese) || formatDate(invoice.data_emissione)}</p>
                <p className="mt-1 text-3xl font-extrabold text-[#304762]">{formatMoney(invoice.totale)}</p>
              </div>
              <PaymentBadge invoice={invoice} />
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-xs text-[#536b86]">
              <div><dt className="font-bold text-[#304762]">Emessa</dt><dd className="mt-0.5">{formatDate(invoice.data_emissione)}</dd></div>
              <div><dt className="font-bold text-[#304762]">Scadenza</dt><dd className="mt-0.5">{formatDate(invoice.data_scadenza)}</dd></div>
              {invoice.destinatario_nome ? <div className="col-span-2"><dt className="font-bold text-[#304762]">Intestata a</dt><dd className="mt-0.5">{invoice.destinatario_nome}</dd></div> : null}
              {Number(invoice.importo_pagato) > 0 ? <div><dt className="font-bold text-[#304762]">Già pagato</dt><dd className="mt-0.5">{formatMoney(invoice.importo_pagato)}</dd></div> : null}
            </dl>
          </Card>
          {invoice.voci?.length ? (
            <section>
              <h2 className="mb-2.5 text-base font-extrabold text-[#273d58]">Voci</h2>
              <Card className="divide-y divide-slate-100 px-4">
                {invoice.voci.map((voce, index) => (
                  <div key={`${voce.descrizione}-${index}`} className="flex items-start justify-between gap-3 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-[#405873]">{voce.descrizione}</p>
                      {Number(voce.quantita) > 1 ? <p className="mt-0.5 text-xs text-[#72839a]">{voce.quantita} × {formatMoney(voce.prezzo_unitario)}</p> : null}
                    </div>
                    <p className="shrink-0 font-bold text-[#304762]">{formatMoney(voce.importo)}</p>
                  </div>
                ))}
              </Card>
            </section>
          ) : null}
          {invoice.pagamenti?.length ? (
            <section>
              <h2 className="mb-2.5 text-base font-extrabold text-[#273d58]">Pagamenti</h2>
              <Card className="divide-y divide-slate-100 px-4">
                {invoice.pagamenti.map((pagamento, index) => (
                  <div key={`${pagamento.data_pagamento}-${index}`} className="flex items-center justify-between py-3 text-sm">
                    <span className="text-[#405873]">{formatDate(pagamento.data_pagamento)}{pagamento.metodo ? ` · ${pagamento.metodo}` : ""}</span>
                    <span className="font-bold text-[#304762]">{formatMoney(pagamento.importo)}</span>
                  </div>
                ))}
              </Card>
            </section>
          ) : null}
          {invoice.pdf_disponibile ? (
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" disabled={Boolean(busy)} onClick={openPdf} className="h-11 rounded-xl">
                Visualizza
              </Button>
              <Button type="button" disabled={Boolean(busy)} onClick={() => downloadPdf(false)} className="h-11 rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">
                <Download className="mr-2 h-4 w-4" />{busy === "download" ? "Download…" : "Scarica PDF"}
              </Button>
            </div>
          ) : (
            <p className="text-center text-xs text-[#72839a]">La copia PDF non è ancora disponibile per questo documento.</p>
          )}
        </>
      ) : (
        <Button type="button" variant="outline" onClick={() => navigate("/family/amministrazione")} className="h-10 w-full rounded-xl">
          Torna all&apos;elenco
        </Button>
      )}
      <InvoicePdfViewer viewer={viewer} onClose={() => setViewer(null)} onSave={(event) => { event.stopPropagation(); downloadPdf(true); }} />
    </div>
  );
}

export default function FamilyAmministrazione() {
  const { fatturaId } = useParams();
  const { api, activeOspiteId } = useFamilyAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (fatturaId || !activeOspiteId) {
      if (!activeOspiteId) setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/ospiti/${activeOspiteId}/amministrazione`)
      .then((response) => {
        if (cancelled) return;
        setData(response.data || {});
        setError("");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || "Non è stato possibile caricare i dati");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, activeOspiteId, fatturaId]);

  if (fatturaId) return <InvoiceDetail fatturaId={fatturaId} />;
  if (loading) return <Loading />;
  const invoices = data?.fatture || [];
  return (
    <div className="space-y-4">
      <PageHeader title="Amministrazione" subtitle="Rette, fatture e situazione pagamenti." />
      <Message error={error} />
      {data?.retta_mensile != null ? (
        <Card className="p-5">
          <p className="text-xs font-semibold text-[#72839a]">Retta mensile di riferimento</p>
          <p className="mt-1 text-3xl font-extrabold text-[#304762]">{formatMoney(data.retta_mensile)}</p>
        </Card>
      ) : null}
      <section>
        <h2 className="mb-2.5 text-base font-extrabold text-[#273d58]">Fatture</h2>
        <div className="space-y-2">
          {invoices.length ? invoices.map((invoice) => (
            <Link key={invoice.id} to={`/family/amministrazione/${invoice.id}`}>
              <Card className="flex items-center gap-3 p-4">
                <Receipt className="h-5 w-5 shrink-0 text-[#6e48b5]" />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-[#304762]">{invoiceTitle(invoice)}</p>
                  <p className="text-xs text-[#72839a]">{formatDate(invoice.data_emissione)} · Scadenza {formatDate(invoice.data_scadenza)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-[#304762]">{formatMoney(invoice.totale)}</p>
                  <div className="mt-1"><PaymentBadge invoice={invoice} /></div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#8ea0b3]" />
              </Card>
            </Link>
          )) : error ? null : (
            <EmptyState title="Nessuna fattura disponibile" text="Le fatture emesse dalla struttura appariranno qui." />
          )}
        </div>
      </section>
    </div>
  );
}
